/**
 * Wave 4 — Admin closing ops: publish wire instructions, set appointment, earnest.
 */

import { useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { toast } from 'sonner';

import {
  useGetClosingOrchestrationQuery,
  useUpdateClosingOrchestrationMutation,
} from '@/apis/extended/closingApi';
import { TasteSectionCard } from '@/components/Common/TasteSectionCard';
import { EarnestMoneyTracker } from '@/components/Property/EarnestMoneyTracker';

export function AdminClosingPanel({ contractId }: { contractId: string }) {
  const { data: closing, isLoading } = useGetClosingOrchestrationQuery(
    contractId,
    { skip: !contractId }
  );
  const [updateClosing, { isLoading: saving }] =
    useUpdateClosingOrchestrationMutation();

  const [wire, setWire] = useState({
    beneficiary: '',
    bankName: '',
    routingNumber: '',
    accountNumber: '',
    reference: '',
  });
  const [appointment, setAppointment] = useState({
    date: '',
    time: '',
    location: '',
    titleContactPhone: '',
  });

  const seedFromClosing = () => {
    if (!closing) return;
    if (closing.wireInstructions) {
      setWire({
        beneficiary: closing.wireInstructions.beneficiary || '',
        bankName: closing.wireInstructions.bankName || '',
        routingNumber: closing.wireInstructions.routingNumber || '',
        accountNumber: closing.wireInstructions.accountNumber || '',
        reference: closing.wireInstructions.reference || '',
      });
    }
    if (closing.closingAppointment) {
      setAppointment({
        date: closing.closingAppointment.date || '',
        time: closing.closingAppointment.time || '',
        location: closing.closingAppointment.location || '',
        titleContactPhone: closing.closingAppointment.titleContactPhone || '',
      });
    }
  };

  if (isLoading) {
    return <Typography variant="body2">Loading closing data…</Typography>;
  }

  const handlePublishWire = async () => {
    try {
      await updateClosing({
        contractId,
        wireInstructions: wire,
      }).unwrap();
      toast.success('Wire instructions published');
    } catch {
      toast.error('Could not publish wire instructions');
    }
  };

  const handleSaveAppointment = async () => {
    try {
      await updateClosing({
        contractId,
        closingAppointment: appointment,
      }).unwrap();
      toast.success('Closing appointment saved');
    } catch {
      toast.error('Could not save appointment');
    }
  };

  return (
    <Stack gap={2}>
      <TasteSectionCard sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Publish wire instructions
          </Typography>
          <Button size="small" onClick={seedFromClosing}>
            Load current
          </Button>
        </Stack>
        <Stack gap={1.5}>
          <TextField
            size="small"
            label="Beneficiary"
            value={wire.beneficiary}
            onChange={(e) => setWire({ ...wire, beneficiary: e.target.value })}
            fullWidth
          />
          <TextField
            size="small"
            label="Bank name"
            value={wire.bankName}
            onChange={(e) => setWire({ ...wire, bankName: e.target.value })}
            fullWidth
          />
          <TextField
            size="small"
            label="Routing number"
            value={wire.routingNumber}
            onChange={(e) =>
              setWire({ ...wire, routingNumber: e.target.value })
            }
            fullWidth
          />
          <TextField
            size="small"
            label="Account number"
            value={wire.accountNumber}
            onChange={(e) =>
              setWire({ ...wire, accountNumber: e.target.value })
            }
            fullWidth
          />
          <TextField
            size="small"
            label="Reference"
            value={wire.reference}
            onChange={(e) => setWire({ ...wire, reference: e.target.value })}
            fullWidth
          />
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => void handlePublishWire()}
          >
            Publish to buyer/seller
          </Button>
        </Stack>
      </TasteSectionCard>

      <TasteSectionCard sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={2}>
          Closing appointment
        </Typography>
        <Stack gap={1.5}>
          <TextField
            size="small"
            label="Date"
            value={appointment.date}
            onChange={(e) =>
              setAppointment({ ...appointment, date: e.target.value })
            }
          />
          <TextField
            size="small"
            label="Time"
            value={appointment.time}
            onChange={(e) =>
              setAppointment({ ...appointment, time: e.target.value })
            }
          />
          <TextField
            size="small"
            label="Location"
            value={appointment.location}
            onChange={(e) =>
              setAppointment({ ...appointment, location: e.target.value })
            }
            fullWidth
          />
          <TextField
            size="small"
            label="Title contact phone"
            value={appointment.titleContactPhone}
            onChange={(e) =>
              setAppointment({
                ...appointment,
                titleContactPhone: e.target.value,
              })
            }
            fullWidth
          />
          <Button
            variant="outlined"
            disabled={saving}
            onClick={() => void handleSaveAppointment()}
          >
            Save appointment
          </Button>
        </Stack>
      </TasteSectionCard>

      <TasteSectionCard sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          Earnest deposits
        </Typography>
        <EarnestMoneyTracker contractId={contractId} />
      </TasteSectionCard>

      {closing?.closedAt && (
        <Typography variant="body2" color="success.main">
          Closed {new Date(closing.closedAt).toLocaleString()}
        </Typography>
      )}
    </Stack>
  );
}
